import React, { useEffect, useMemo, useState } from 'react';
import { Divider } from 'antd';
import { useDispatch, useIntl, useSelector } from 'umi';
import { closestCenter, DndContext, DragOverlay } from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import isEmpty from 'lodash/isEmpty';

import * as PreferencesSelectors from '@/selectors/preferencesSelectors';
import * as ExtraFieldsSelectors from '@/selectors/extraFieldsSelectors';
import SearchBar from './SearchBar';
import FieldList from './FieldList';
import { F, getIntlTranslation } from '@/utils/intl';
import Property from '@/components/Kanban/VisibilitySettings/Property';
import {
  INITIAL_PROPERTY_VISIBILITY_SETTINGS,
  LEAD_VISIBILITY_SETTINGS_PLACES,
} from '@/components/Kanban/VisibilitySettings/constants';
import { useHasMarketingDataAccess } from '@/utils/hooks';
import { LEAD_PROPERTY_COLUMN_SETTINGS } from '@/components/Lead/LeadProperties';
import { CUSTOMER_PROPERTY_COLUMN_SETTINGS } from '@/components/CustomerDrawer/constants';
import { APPOINTMENT_PROPERTY_COLUMN_SETTINGS } from '@/components/Appointment/AppointmentProperties';
import { isMarketingDataViewField } from '@/utils/marketingDataView';

const mergeFields = (fields, extraFields) => {
  const mapFieldIdToObject = (fieldId) => {
    if (fieldId.startsWith('extraField:')) {
      const uuid = fieldId.split('extraField:')[1];
      const fieldType = extraFields.find(f => f.uuid === uuid);
      return fieldType ? { ...fieldType, type: 'extraField', id: fieldId } : null;
    }
    return fieldId;
  };

  const shownFields = fields.shown.map(mapFieldIdToObject).filter(Boolean);
  const hiddenFields = fields.hidden.map(mapFieldIdToObject).filter(Boolean);

  const hiddenExtraFieldsNotInSettings = extraFields
    .filter(f => !fields.shown.includes(`extraField:${f.uuid}`) && !fields.hidden.includes(`extraField:${f.uuid}`))
    .map(f => ({ ...f, type: 'extraField', id: `extraField:${f.uuid}` }));

  return {
    shownFields,
    hiddenFields: [...hiddenFields, ...hiddenExtraFieldsNotInSettings],
  };
};

export default function PropertyVisibilitySettings({hideExtraFields = false, hide = false, place = LEAD_VISIBILITY_SETTINGS_PLACES.LEAD_BOARD }: { hide?: boolean, hideExtraFields?: boolean, place?: string }) {
  const dispatch = useDispatch();
  const [search, setSearch] = useState('');
  const [activeItem, setActiveItem] = useState(null);
  const hasMarketingDataAccess = useHasMarketingDataAccess()
  const intl = useIntl()
  const visibilitySettings = useSelector((state: DefaultRootState) =>
    PreferencesSelectors.selectLeadsVisibilitySettings(state, place)
  ) || INITIAL_PROPERTY_VISIBILITY_SETTINGS;

  const extraFieldTypes = useSelector(ExtraFieldsSelectors.selectVisibleExtraFieldTypesList);


  const filterFields = (fields, search) => {
    return fields.filter((field) => {
      if (!hasMarketingDataAccess && isMarketingDataViewField(field)) return false;

      if (hideExtraFields && field.type === 'extraField') return false;

      if (isEmpty(search)) return true

      let label = '';
      if (typeof field === 'string') {
        const settings =
          LEAD_PROPERTY_COLUMN_SETTINGS[field] ||
          CUSTOMER_PROPERTY_COLUMN_SETTINGS[field] ||
          APPOINTMENT_PROPERTY_COLUMN_SETTINGS[field];

        if (settings?.title) {
          label = getIntlTranslation(intl, {
            ...settings.title,
            defaultMessage: settings.title.defaultMessage || field,
          });
        } else {
          label = intl.formatMessage({
            id: `pages.lead.fields.${field}`,
            defaultMessage: field,
          });
        }
      } else {
        label = field.label || field.name || '';
      }

      return label.toLowerCase().includes(search.toLowerCase());
    });
  };


  useEffect(() => {
    dispatch({ type: 'extraFields/fetchExtraFieldTypes' });
  }, [dispatch]);

  const updateSettings = (setter: (prev: any) => any) => {
    dispatch({
      type: 'preferences/update',
      payload: {
        key: place,
        value: setter(visibilitySettings),
      },
    });
  };

  const handleFieldToggle = (fieldId: string, isVisible: boolean) => {
    updateSettings((prev) => {
      if (isVisible) {
        return {
          hidden: prev.hidden.filter(f => f !== fieldId),
          shown: [...prev.shown, fieldId],
        };
      } else {
        return {
          shown: prev.shown.filter(f => f !== fieldId),
          hidden: [...prev.hidden, fieldId],
        };
      }
    });
  };

  const handleDragStart = (event: any) => {
    setActiveItem(event.active.id);
  };

  const handleDragEnd = (event: any) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      updateSettings((prev) => ({
        ...prev,
        shown: arrayMove(prev.shown, prev.shown.indexOf(active.id), prev.shown.indexOf(over.id)),
      }));
    }
    setActiveItem(null);
  };

  const { shownFields, hiddenFields } = useMemo(() => mergeFields(visibilitySettings, extraFieldTypes), [visibilitySettings, extraFieldTypes]);
  const filteredShownFields = useMemo(
    () => filterFields(shownFields, search),
    [shownFields, search, hasMarketingDataAccess],
  );
  const filteredHiddenFields = useMemo(
    () => filterFields(hiddenFields, search),
    [hiddenFields, search, hasMarketingDataAccess],
  );

  if (hide) return null;


  return (
    <div>
      <SearchBar value={search} onSearch={setSearch} />

      <Divider><F id={'pages.lead.visibilitySettings.shown'} defaultMessage={'Shown'} /></Divider>
      <DndContext
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={filteredShownFields.map(f => f.id)} strategy={verticalListSortingStrategy}>
          <FieldList fields={filteredShownFields} onToggle={handleFieldToggle} isVisible />
        </SortableContext>

        <DragOverlay>
          {activeItem && (
            <Property
              languageKey={activeItem}
              isVisible
              hasSort={false}
              isDragging
            />
          )}
        </DragOverlay>
      </DndContext>

      <Divider><F id={'pages.lead.visibilitySettings.hidden'} defaultMessage={'Hidden'} /></Divider>
      <FieldList groupBy fields={filteredHiddenFields} onToggle={handleFieldToggle} isVisible={false} />
    </div>
  );
}
